import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import ServiceManager from 'services/ServiceManager';
import { useTranslation } from 'react-i18next';
import { useFormatting } from 'locale';
import { downloadDmsDocument } from 'utils/downloadDmsDocument';
import { uniq } from 'lodash';
import {
    adaptDocuments, adaptUnifiedDocuments, getNames, UnifiedDocument,
} from '../adapters/adaptDocuments';
import { usePortfolios } from './usePortfolios';
import { generateDFSClientId } from '../../../../../utils';
import { DOCUMENT_SOURCE } from '../constants';
import { DEFAULT_PAGE_SIZE } from '../../../../DocLibraryPage/constants/const';
import { getMimeTypeFromBase64File } from '../utils';

// --- Interfaces ---

interface Paging {
    Page?: number;
    PageSize?: number;
    TotalCount?: number;
}

interface DocumentData {
    id: string;
    filename: string;
    file?: string;
    mimeType?: string;
    originalFilename?: string;
    createDate?: string;
    businessUnit?: string;
    category?: string;
    name: string;
    [key: string]: unknown; // For adapted properties
}

interface UseDocumentsProps {
    clientId: string | number;
    currentPagination: number;
    tab: 'general' | 'personal';
    businessUnitFilter?: string;
    categoryFilter?: string;
}

interface GeneralParams {
    isActive: boolean;
    sortBy: string;
    sortOrder: string;
    page: number;
    pageSize: number;
    searchBy: string;
    documentTypeId: number;
}

const sortByDateDesc = (documents: UnifiedDocument[] | undefined | null) => {
    if (!Array.isArray(documents)) return documents;

    return [...documents].sort((a, b) => {
        const dateA = a.createDate ? new Date(a.createDate).getTime() : 0;
        const dateB = b.createDate ? new Date(b.createDate).getTime() : 0;

        return dateB - dateA;
    });
};

// --- Hook ---

export const useDocuments = ({
    clientId, currentPagination, tab, businessUnitFilter, categoryFilter,
}: UseDocumentsProps) => {
    const [documentDownloadId, setDocumentDownloadId] = useState<string>('');
    const [downloadError, setDownloadError] = useState<Error | null>(null);

    const { t, i18n } = useTranslation();
    const { getFormattedDate } = useFormatting();
    const { getPortfolios } = usePortfolios({ clientId });

    const generalParams = useMemo<GeneralParams>(() => ({
        isActive: true,
        sortBy: 'CreateDate',
        sortOrder: 'Descending',
        page: currentPagination,
        pageSize: DEFAULT_PAGE_SIZE,
        searchBy: 'IncludeShared',
        documentTypeId: 2,
    }), [currentPagination]);

    const handleDownloadFile = useCallback((documentData: DocumentData) => {
        const { filename } = documentData;
        const { newBlob } = downloadDmsDocument(documentData, { name: filename, saveFile: true });
        const url = URL.createObjectURL(newBlob);

        window.open(url, '_blank', 'noopener,noreferrer');
    }, []);

    const downloadDocument = useCallback(async (
        documentId?: string,
        source?: string | number,
        customerId?: string,
        filename?: string,
        productId?: string,
    ) => {
        setDocumentDownloadId(documentId as string);
        setDownloadError(null);
        try {
            if (!source || source === DOCUMENT_SOURCE.dmsDocument) {
                const { data: documentData } = (await ServiceManager.documents('getDocumentById', [documentId]) as any);

                handleDownloadFile(documentData as DocumentData);
            } else if (source === DOCUMENT_SOURCE.lpzDocument) {
                const { data: documentData } = (await ServiceManager.customDocumentsService('getDocumentPdf', [{
                    contactId: clientId,
                    DocumentId: documentId,
                    CustomerId: customerId,
                    ProductId: productId,
                }]) as any);

                handleDownloadFile({
                    id: documentId as string,
                    file: documentData.pdf,
                    mimeType: getMimeTypeFromBase64File(documentData.pdf),
                    originalFilename: filename,
                    name: filename || 'document.pdf',
                    filename: filename || 'document.pdf',
                });
            }
        } catch (err: any) {
            setDownloadError(err);
        }
        setDocumentDownloadId('');
    }, [handleDownloadFile, clientId]);

    // --- General documents query ---
    const {
        data: generalQueryData,
        isLoading: isGeneralLoading,
        error: generalError,
    } = useQuery({
        queryKey: ['documents_general', generalParams],
        queryFn: async () => {
            const response = await ServiceManager.customDocumentsService('getUnifiedDocuments', [generalParams]) as any;
            const documentsData = response.data;
            const userPortfolios = await getPortfolios({ documents: documentsData });

            return {
                documentsData, userPortfolios, page: documentsData?.page, totalCount: documentsData?.totalCount,
            };
        },
        enabled: tab === 'general',
    });

    const generalData = useMemo<UnifiedDocument[]>(() => {
        if (!generalQueryData) return [];

        return adaptDocuments(
            generalQueryData.documentsData,
            downloadDocument,
            t,
            i18n,
            getFormattedDate,
            (generalQueryData.userPortfolios as any) || [],
            tab,
        ) as unknown as UnifiedDocument[];
    }, [generalQueryData, downloadDocument, t, i18n, getFormattedDate, tab]);

    const generalPaging = useMemo<Paging>(() => ({
        Page: generalQueryData?.page,
        PageSize: DEFAULT_PAGE_SIZE,
        TotalCount: generalQueryData?.totalCount,
    }), [generalQueryData]);

    const generalNames = useMemo(() => getNames(generalData as any), [generalData]);

    // --- Personal documents query ---
    const {
        data: rawPersonalData,
        isLoading,
        error: personalError,
        refetch: refetchPersonal,
    } = useQuery({
        queryKey: ['documents_personal', clientId, i18n.language],
        queryFn: async () => {
            const { data: response } = await ServiceManager.customDocumentsService('getUnifiedDocuments', [{ contactId: clientId, language: i18n.language }]) as any;

            return sortByDateDesc(response) as UnifiedDocument[];
        },
        enabled: tab === 'personal',
        staleTime: 1000 * 60 * 30, // 30 minutes
        gcTime: 1000 * 60 * 60, // 1 hour
    });

    // Adapt raw personal data; recomputes when documentDownloadId changes (per-row download state)
    const adaptedPersonalData = useMemo<UnifiedDocument[] | null>(() => {
        if (!rawPersonalData) return null;

        return adaptUnifiedDocuments({
            data: rawPersonalData,
            dfsClientId: generateDFSClientId(clientId),
            downloadDocument,
            documentDownloadId,
        }) as unknown as UnifiedDocument[];
    }, [rawPersonalData, clientId, downloadDocument, documentDownloadId]);

    const filteredData = useMemo<UnifiedDocument[]>(() => {
        if (!adaptedPersonalData) return [];

        return adaptedPersonalData.filter(f => (!businessUnitFilter || f.businessUnit === businessUnitFilter)
            && (!categoryFilter || f.category === categoryFilter));
    }, [adaptedPersonalData, businessUnitFilter, categoryFilter]);

    const personalPaging = useMemo<Paging>(() => ({
        Page: 1,
        PageSize: DEFAULT_PAGE_SIZE,
        TotalCount: filteredData.length,
    }), [filteredData]);

    const names = useMemo(() => getNames(filteredData as any), [filteredData]);

    const categories = useMemo(
        () => uniq(adaptedPersonalData?.map(m => m.category).filter((c): c is string => !!c)) ?? null,
        [adaptedPersonalData],
    );

    const businessUnits = useMemo(
        () => uniq(adaptedPersonalData?.map(m => m.businessUnit).filter((b): b is string => !!b)) ?? null,
        [adaptedPersonalData],
    );

    return {
        data: filteredData,
        generalData,
        names: names.concat(generalNames),
        isLoading,
        isGeneralLoading,
        pageSize: DEFAULT_PAGE_SIZE,
        personalPaging,
        generalPaging,
        error: personalError ?? generalError ?? downloadError ?? null,
        categories,
        businessUnits,
        getUnifiedDocuments: refetchPersonal,
    };
};
